import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, Card, CardContent, Button, useTheme, CircularProgress } from '@mui/material';
import apiService from '../utils/api';

// 图标组件映射
const IconComponents = {
  calculator: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" />
      <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.5" />
      <line x1="12" y1="15" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="19" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="15" x2="8" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="19" x2="8" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="16" y1="15" x2="16" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="16" y1="19" x2="16" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  todo: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 6L20 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12L20 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 18L20 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 6L4 6C3.44772 6 3 6.44772 3 7L3 7C3 7.55228 3.44772 8 4 8L5 8C5.55228 8 6 7.55228 6 7L6 7C6 6.44772 5.55228 6 5 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 12L4 12C3.44772 12 3 12.4477 3 13L3 13C3 13.5523 3.44772 14 4 14L5 14C5.55228 14 6 13.5523 6 13L6 13C6 12.4477 5.55228 12 5 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 18L4 18C3.44772 18 3 18.4477 3 19L3 19C3 19.5523 3.44772 20 4 20L5 20C5.55228 20 6 19.5523 6 19L6 19C6 18.4477 5.55228 18 5 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  weather: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.5 16C3.29086 16 1.5 14.2091 1.5 12C1.5 9.79086 3.29086 8 5.5 8C5.69176 8 5.8798 8.01212 6.06301 8.03544C6.56537 5.73303 8.61013 4 11 4C13.7614 4 16 6.23858 16 9C16 9.11409 15.994 9.22724 15.9823 9.33916C16.7993 9.12252 17.6648 9 18.5625 9C20.5745 9 22.4354 9.68182 23.9253 10.8297" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 16L16 16C18.2091 16 20 14.2091 20 12C20 11.4459 19.8836 10.9175 19.673 10.4393C19.3854 9.78293 18.9265 9.22065 18.3536 8.77631C17.7806 8.33197 17.1069 8 16.4142 8C16.2748 8 16.1373 8.00825 16 8.0242C15.7977 6.88228 15.2226 5.83468 14.4142 5.02513C13.6332 4.2441 12.6145 3.66312 11.5209 3.2388C10.3366 2.78225 9.12772 2.56839 8 2.50383" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 16V17C8 19.2091 9.79086 21 12 21C14.2091 21 16 19.2091 16 17V16" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
};

// 渲染图标的函数，支持预定义SVG图标和图片URL
const renderIcon = (iconKey, imageWidth = 250, imageHeight = 250) => {
  // 检查是否是预定义的SVG图标
  if (IconComponents[iconKey]) {
    return IconComponents[iconKey];
  }
  
  // 检查是否是图片URL (以 / 或 http 开头)
  if (typeof iconKey === 'string' && (iconKey.startsWith('/') || iconKey.startsWith('http'))) {
    return (
      <img 
        src={iconKey} 
        alt="icon" 
        style={{ 
          width: `${imageWidth}px`, 
          height: `${imageHeight}px`, 
          objectFit: 'contain',
          maxWidth: '100%'
        }} 
      />
    );
  }
  
  // 默认返回calculator图标
  return IconComponents.calculator;
};

const Tools = () => {
  const theme = useTheme();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        // 使用API服务获取开发案例
        const data = await apiService.getDevCases();
        setCases(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching cases:", err);
        setError(err.message);
        setLoading(false);
        
        // 如果API请求失败，使用默认数据
        setCases([
          {
            id: 1,
            name: "在线计算器",
            description: "一个简单易用的网页计算器，支持基本的数学运算和高级函数计算，界面简洁直观。",
            icon: "calculator",
            bgcolor: "rgba(77, 124, 111, 0.05)",
            link: "https://example.com/calculator",
            button_text: "查看工具",
            image_width: 250,
            image_height: 250
          },
          {
            id: 2,
            name: "待办事项",
            description: "一个简洁的待办事项管理工具，帮助你高效记录和管理日常任务，提高工作和生活效率。",
            icon: "todo",
            bgcolor: "rgba(122, 160, 149, 0.05)",
            link: "https://example.com/todo",
            button_text: "查看工具",
            image_width: 250,
            image_height: 250
          },
          {
            id: 3,
            name: "天气查询",
            description: "查询全球各地实时天气情况，支持未来七天的天气预报以及空气质量指数等数据展示。",
            icon: "weather",
            bgcolor: "rgba(44, 90, 77, 0.05)",
            link: "https://example.com/weather",
            button_text: "查看工具",
            image_width: 250,
            image_height: 250
          }
        ]);
      }
    };

    fetchCases();
  }, []);
  
  return (
    <Box 
      sx={{ 
        background: '#ffffff',
        py: { xs: 8, md: 12 }
      }}
      id="tools"
    >
      <Container maxWidth="lg">
        <Box className="text-center mb-16">
          <Typography 
            variant="h2" 
            className="text-3xl sm:text-4xl"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 3
            }}
          >
            开发案例
          </Typography>
          
          <Typography
            variant="body1"
            className="max-w-2xl mx-auto"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '1.125rem',
              lineHeight: 1.6,
              textAlign: 'center',
              margin: '0 auto',
              maxWidth: '600px',
              mb: 8
            }}
          >
            一人团队
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error">加载失败: {error}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
            {cases.map((item) => (
              <Box key={item.id} sx={{ width: '340px' }}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%', 
                    width: '100%',
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: '16px',
                    border: '1px solid rgba(0,0,0,0.05)',
                    background: '#ffffff',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 30px rgba(0,0,0,0.05)',
                    }
                  }}
                >
                  <Box 
                    sx={{
                      p: 5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      background: item.bgcolor,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* 背景装饰 */}
                    <Box 
                      sx={{
                        position: 'absolute',
                        width: '140%',
                        height: '140%',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
                        top: '-20%',
                        left: '-20%',
                      }}
                    />
                    
                    <Box 
                      sx={{
                        color: theme.palette.primary.main,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {renderIcon(item.icon, item.image_width, item.image_height)}
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography 
                      variant="h5" 
                      sx={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        mb: 2,
                        textAlign: 'center',
                        color: theme.palette.text.primary
                      }}
                    >
                      {item.name}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: theme.palette.text.secondary,
                        textAlign: 'center',
                        mb: 3,
                        lineHeight: 1.6,
                        height: 70,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {item.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        component="a"
                        href={item.link}
                        target={item.link.startsWith('/') ? '_self' : '_blank'}
                        rel={item.link.startsWith('/') ? undefined : 'noopener noreferrer'}
                        sx={{
                          borderRadius: '9999px',
                          px: 3,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: 'none',
                          }
                        }}
                      >
                        {item.button_text || '查看工具'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Tools; 